import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {QueryBlockItem} from '../../../models/query-block';
import {CdkDragDrop, CdkDragEnter, CdkDragExit, moveItemInArray} from '@angular/cdk/drag-drop';
import {SearchKeywordItemComponent} from '../search-keyword-item/search-keyword-item.component';
import {SearchKeywordListComponent} from '../search-keyword-list/search-keyword-list.component';
import {ResearchKeywordDTO} from '../../../dto/research-keywords';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonToggleChange, MatButtonToggleModule} from '@angular/material/button-toggle';
import {QueryHelperService} from '../../../helper/query-helper.service';
import {ResearchSource} from '../../../dto/research-paper';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {distinctUntilChanged} from 'rxjs';

@Component({
  selector: 'app-search-keyword-group',
  imports: [
    SearchKeywordItemComponent,
    SearchKeywordListComponent,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CdkTextareaAutosize,
    ReactiveFormsModule,
  ],
  templateUrl: './search-keyword-group.component.html',
  styleUrl: './search-keyword-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchKeywordGroupComponent implements OnInit, AfterViewInit {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly queryHelperService: QueryHelperService = inject(QueryHelperService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly keywords = input.required<ResearchKeywordDTO[]>();
  readonly query = input<string>();
  readonly researchSource = input<ResearchSource>(ResearchSource.PUBMED)
  readonly formGroup = input<FormGroup>();

  showBuilder = signal<boolean>(false);
  parentItem: QueryBlockItem = new QueryBlockItem();

  public get connectedDropListsIds(): string[] {
    return this.getIdsRecursive(this.parentItem).reverse();
  }

  public ngOnInit() {
    const initialQuery = this.formGroup()?.get('query')?.value ?? this.query();
    if (initialQuery) {
      this.parentItem = this.queryHelperService.constructQuery(initialQuery, this.keywords(), this.researchSource());
    }
  }

  ngAfterViewInit(): void {
    if (!this.formGroup()) {
      return;
    }
    this.formGroup()!.get('query')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.parentItem = this.queryHelperService.constructQuery(query, this.keywords(), this.researchSource());
        this.cdr.detectChanges();
      });
  }

  public onItemDeleted(item: QueryBlockItem) {
    this.removeItemFromTree(this.parentItem, item);
    this.cleanupSingleChildGroups(this.parentItem);
    this.updateQuery();
    this.cdr.detectChanges();
  }

  private removeItemFromTree(parent: QueryBlockItem, itemToRemove: QueryBlockItem): boolean {
    const index = parent.children.findIndex(child => child.uId === itemToRemove.uId);
    if (index !== -1) {
      parent.children.splice(index, 1);
      return true;
    }
    for (const child of parent.children) {
      if (child.children && child.children.length > 0) {
        if (this.removeItemFromTree(child, itemToRemove)) {
          return true;
        }
      }
    }
    return false;
  }

  private cleanupSingleChildGroups(item: QueryBlockItem) {
    if (!item.children || item.children.length === 0) {
      return;
    }

    for (let i = 0; i < item.children.length; i++) {
      this.cleanupSingleChildGroups(item.children[i]);
    }

    if (item.children.length === 1) {
      const onlyChild = item.children[0];
      if (onlyChild.keyword) {
        item.keyword = onlyChild.keyword;
        item.children = [];
      } else if (onlyChild.children && onlyChild.children.length > 0) {
        item.children = [...onlyChild.children];
        item.operator = onlyChild.operator || item.operator;
      }
    }
  }

  public onDragDrop(event: CdkDragDrop<QueryBlockItem>) {
    event.container.element.nativeElement.classList.remove('active');
    if (this.canBeDropped(event)) {
      const movingItem: QueryBlockItem = event.item.data;
      const sourceContainer = event.previousContainer.data;
      const targetContainer = event.container.data;
      // move into empty parent

      // 1. new subgroup is created
      if (targetContainer.children.length === 0 && targetContainer.keyword) {
        const groupNode = new QueryBlockItem({
          keyword: targetContainer.keyword,
          children: []
        });
        targetContainer.keyword = undefined;
        targetContainer.children.push(groupNode);
        targetContainer.children.push(movingItem);
      } else {
        targetContainer.children.push(movingItem);
      }

      if (sourceContainer.children && sourceContainer.children.length > 0) {
        sourceContainer.children = sourceContainer.children.filter(
          (child) => child.uId !== movingItem.uId
        );
      }

      // 2. group is not needed anymore
      if (sourceContainer.children && sourceContainer.children.length === 1) {
        const lastItem = sourceContainer.children[0];

        if (lastItem.keyword) {
          sourceContainer.keyword = lastItem.keyword;
          sourceContainer.children = [];
        } else if (lastItem.children && lastItem.children.length > 0) {
          sourceContainer.children = [...lastItem.children];
        }
      }
    } else {
      // move in the same group
      moveItemInArray(
        event.container.data.children,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.updateQuery();
  }

  protected updateQuery(): void {
    this.formGroup()?.get('query')?.setValue(this.finalQuery);
  }

  public get finalQuery(): string {
    return this.queryHelperService.buildQueryString(this.parentItem, this.researchSource());
  }

  public changeBlock(event: MatButtonToggleChange) {
    this.showBuilder.set(event.value);
  }


  private getIdsRecursive(item: QueryBlockItem): string[] {
    let ids = [item.uId, "keyword-source-list"];
    item.children.forEach((childItem) => {
      ids = ids.concat(this.getIdsRecursive(childItem))
    });
    return ids;
  }

  private canBeDropped(event: CdkDragDrop<QueryBlockItem, QueryBlockItem>): boolean {
    const movingItem: QueryBlockItem = event.item.data;

    return event.previousContainer.id !== event.container.id
      && this.isNotSelfDrop(event)
      && !this.hasChild(movingItem, event.container.data);
  }

  private isNotSelfDrop(event: CdkDragDrop<QueryBlockItem> | CdkDragEnter<QueryBlockItem> | CdkDragExit<QueryBlockItem>): boolean {
    if (!event.container.data) {
      return true;
    }
    return event.container.data.uId !== event.item.data.uId;
  }

  private hasChild(parentItem: QueryBlockItem, childItem: QueryBlockItem): boolean {
    const hasChild = parentItem.children.some((item) => item.uId === childItem.uId);
    return hasChild ? true : parentItem.children.some((item) => this.hasChild(item, childItem));
  }

}
